import { getRuntimePlatform } from '@/utils/runtimePlatform'

const TEXT_INPUT_TYPES = ['text', 'search', 'tel', 'email', 'url', 'password', 'number']
const MAP_FORM_PATH = /^\/(?:route\/(?:add|edit\/[^/]+)|nofly\/(?:setNofly|setRestrictfly))\/?$/

const EXTRA_GAP = 18
const ESTIMATED_KEYBOARD_RATIO = 0.46
const KEYBOARD_WAIT_TIMES = [40, 120, 240, 420, 680, 980]

let installed = false
let activeElement = null
let timers = []
let currentRouter = null
let scrollPositions = []

function isEditableElement (element) {
  if (!element || !element.matches || element.disabled || element.readOnly) return false
  if (element.closest && element.closest('.el-select, .el-cascader, .el-date-editor')) return false
  if (element.matches('textarea, [contenteditable="true"]')) return true
  return element.matches('input') && TEXT_INPUT_TYPES.includes((element.type || 'text').toLowerCase())
}

function shouldAvoidKeyboard () {
  return document.documentElement.classList.contains('is-cordova-android') &&
    !(currentRouter && MAP_FORM_PATH.test(currentRouter.currentRoute.path))
}

function getViewportBottom () {
  if (window.visualViewport) {
    return window.visualViewport.offsetTop + window.visualViewport.height
  }
  return window.innerHeight || document.documentElement.clientHeight
}

function getViewportTop () {
  return window.visualViewport ? window.visualViewport.offsetTop : 0
}

function getViewportHeight () {
  return window.innerHeight || document.documentElement.clientHeight
}

function getKeyboardInset () {
  const viewportHeight = getViewportHeight()
  const visualViewport = window.visualViewport
  const resizedInset = visualViewport ? Math.max(0, viewportHeight - visualViewport.height - visualViewport.offsetTop) : 0
  if (resizedInset > 80) return resizedInset
  return Math.round(viewportHeight * ESTIMATED_KEYBOARD_RATIO)
}

function syncKeyboardState (active) {
  const root = document.documentElement
  root.classList.toggle('android-keyboard-active', active)
  if (active) root.style.setProperty('--android-keyboard-avoid-bottom', `${getKeyboardInset() + EXTRA_GAP}px`)
  else root.style.removeProperty('--android-keyboard-avoid-bottom')
}

function getScrollableParent (element) {
  let current = element && element.parentElement
  while (current && current !== document.body && current !== document.documentElement) {
    const style = window.getComputedStyle(current)
    const overflowY = style.overflowY
    const canScroll = /(auto|scroll|overlay)/.test(overflowY) && current.scrollHeight > current.clientHeight + 1
    if (canScroll) return current
    current = current.parentElement
  }
  return document.scrollingElement || document.documentElement
}

function rememberScrollPosition (container) {
  if (scrollPositions.some(item => item.container === container)) return
  scrollPositions.push({
    container,
    top: container === window ? (window.scrollY || window.pageYOffset || 0) : container.scrollTop
  })
}

function restoreScrollPosition () {
  scrollPositions.forEach(({ container, top }) => {
    if (container === window) window.scrollTo({ top, behavior: 'instant' })
    else container.scrollTo({ top, behavior: 'instant' })
  })
  scrollPositions = []
}

function scrollContainerBy (container, delta) {
  const scrollTarget = !container || container === document.scrollingElement || container === document.documentElement || container === document.body
    ? window
    : container
  rememberScrollPosition(scrollTarget)
  scrollTarget.scrollTo({ top: (scrollTarget === window ? (window.scrollY || window.pageYOffset || 0) : scrollTarget.scrollTop) + delta, behavior: 'instant' })
}

function keepElementAboveKeyboard (element) {
  if (!isEditableElement(element) || !shouldAvoidKeyboard()) return

  const rect = element.getBoundingClientRect()
  const viewportTop = getViewportTop()
  const viewportBottom = getViewportBottom()
  const viewportHeight = getViewportHeight()
  const visualViewportShrunk = window.visualViewport && window.visualViewport.height < viewportHeight - 80
  const safeBottom = (visualViewportShrunk ? viewportBottom : viewportBottom - getKeyboardInset()) - EXTRA_GAP
  const safeTop = viewportTop + EXTRA_GAP

  if (rect.bottom > safeBottom) {
    scrollContainerBy(getScrollableParent(element), rect.bottom - safeBottom)
  } else if (rect.top < safeTop) {
    scrollContainerBy(getScrollableParent(element), rect.top - safeTop)
  }
}
function clearTimers () {
  timers.forEach(timer => clearTimeout(timer))
  timers = []
}

function scheduleKeyboardAvoidance (element) {
  clearTimers()
  keepElementAboveKeyboard(element)
  timers = KEYBOARD_WAIT_TIMES.map(delay => setTimeout(() => keepElementAboveKeyboard(element), delay))
}

function handleFocusIn (event) {
  const target = event.target
  if (!isEditableElement(target) || !shouldAvoidKeyboard()) return
  if (activeElement && activeElement !== target) restoreScrollPosition()
  activeElement = target
  syncKeyboardState(true)
  scheduleKeyboardAvoidance(target)
}

function handleFocusOut (event) {
  if (event.target !== activeElement) return
  activeElement = null
  clearTimers()
  syncKeyboardState(false)
  restoreScrollPosition()
}

function handleViewportResize () {
  if (activeElement) syncKeyboardState(true)
  if (activeElement) scheduleKeyboardAvoidance(activeElement)
}

export function installAndroidKeyboardAvoidance (router) {
  currentRouter = router
  if (installed) return
  installed = true

  document.addEventListener('focusin', handleFocusIn, true)
  document.addEventListener('focusout', handleFocusOut, true)
  if (router) {
    router.afterEach(() => {
      activeElement = null
      clearTimers()
      syncKeyboardState(false)
      scrollPositions = []
    })
  }

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleViewportResize)
    window.visualViewport.addEventListener('scroll', handleViewportResize)
  } else {
    window.addEventListener('resize', handleViewportResize)
  }

  const syncEnabled = () => {
    const runtime = getRuntimePlatform()
    if (!runtime.isCordovaAndroid) {
      activeElement = null
      clearTimers()
      syncKeyboardState(false)
      restoreScrollPosition()
    }
  }

  syncEnabled()
  document.addEventListener('deviceready', syncEnabled, false)
}
